/**
 * @fileoverview The beds in use, by id and name only, for a bed drop-down (W24 Job H): Add a plant's, the packet
 * review's and the photo tray's bed lists read this short list (GET /locations?short=1) rather than every bed with its
 * plants, notes, photos, diary and share counts. The server sends only the beds in use, in the full list's order (the
 * Seed vault last), so nothing is filtered here.
 *
 * Where: the bed drop-downs (PlantFormDialog, PacketReview, PhotoTray, TrayPiles; switched over by W24 Job B's branch).
 * API: GET /locations?short=1&lang= (getBedNames).
 * Settings: none; kept under the beds' own key ([BEDS_KEY, 'names', language], shared/hooks/useBeds), so every write
 * that reads the beds again (a bed saved, added, archived or removed invalidates 'locations') reads the names too.
 *
 * @module useBedNames
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getBedNames, type BedName } from '@/shared/api/locations';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { BEDS_KEY } from '@/shared/hooks/useBeds';

/**
 * The beds in use, by id and name, in the server's order.
 *
 * @param enabled - Whether to read them now (a closed dialog waits); true by default.
 * @returns The query: `data` is the beds' ids and names (undefined until they arrive).
 */
export function useBedNames(enabled = true): UseQueryResult<BedName[], Error> {
  const { i18n: { language } } = useTranslation();
  return useQuery<BedName[], Error>({
    queryKey: [BEDS_KEY, 'names', language],
    queryFn: () => getBedNames(),
    enabled,
  });
}
